import React, { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2 } from 'lucide-react';

interface BankConsentsInputProps {
  value?: { 
    bank_balance_promise?: boolean;
    allow_lending_entity_credit_review?: boolean;
  };
  onChange?: (value: { 
    bank_balance_promise?: boolean;
    allow_lending_entity_credit_review?: boolean;
  }) => void;
  disabled?: boolean;
}

/**
 * Atomic component for Bank Consents checkboxes
 * Component ID: LOS_BankConsentsInput_432
 * Getter API: LOS_GetBankConsents_421
 * Setter API: LOS_SetBankConsents_422
 */
const BankConsentsInput: React.FC<BankConsentsInputProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const bankBalancePromise = value?.bank_balance_promise || false;
  const allowCreditReview = value?.allow_lending_entity_credit_review || false;

  const handleBalancePromiseChange = useCallback((checked: boolean) => {
    if (onChange) {
      onChange({
        bank_balance_promise: checked,
        allow_lending_entity_credit_review: value?.allow_lending_entity_credit_review || false
      });
    }
  }, [onChange, value]);

  const handleCreditReviewChange = useCallback((checked: boolean) => {
    if (onChange) {
      onChange({
        bank_balance_promise: value?.bank_balance_promise || false,
        allow_lending_entity_credit_review: checked
      });
    }
  }, [onChange, value]);

  const allConsentsGiven = bankBalancePromise && allowCreditReview;

  return (
    <div className="space-y-4">
      {/* Bank Balance Promise Consent */}
      <div className="flex items-start space-x-2">
        <Checkbox
          id="bank_balance_promise"
          checked={bankBalancePromise}
          onCheckedChange={handleBalancePromiseChange}
          disabled={disabled}
        />
        <div className="space-y-1">
          <Label
            htmlFor="bank_balance_promise"
            className="text-sm font-normal leading-tight cursor-pointer"
          >
            I promise that the bank account has sufficient balance for the mandate amount and will maintain it throughout the loan tenure.
          </Label>
        </div>
      </div>

      {/* Credit Review Consent */}
      <div className="flex items-start space-x-2">
        <Checkbox
          id="allow_lending_entity_credit_review"
          checked={allowCreditReview}
          onCheckedChange={handleCreditReviewChange}
          disabled={disabled}
        />
        <div className="space-y-1">
          <Label
            htmlFor="allow_lending_entity_credit_review"
            className="text-sm font-normal leading-tight cursor-pointer"
          >
            I allow the lending entity to review my credit history and bank statements for loan processing.
          </Label>
        </div>
      </div>

      {/* Success Message */}
      {allConsentsGiven && (
        <Alert className="bg-intellend-green/5 border-intellend-green/20">
          <CheckCircle2 className="h-4 w-4 text-intellend-green" />
          <AlertDescription className="text-intellend-green">
            All required consents have been provided. You can proceed to the next step.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default BankConsentsInput;
